import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Extraction } from "../../../api/knowledgeData/knowledgeDataDecoders";
import { parseDocumentVersionRef } from "../../../shared/references/contentReferences";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import Tabs from "../../../ui/navigation/Tabs";
import { TextWithStatements, useSourceDocument, type PlacedStatement, type RegestResult } from "../../sources";
import ProvenancePanel from "../provenance/ProvenancePanel";
import ABoxTechnicalDetails from "./ABoxTechnicalDetails";
import { groupIndividuals, type UnrecordedInstanceData } from "./aboxViewModel";

/** Instance data to show: a recorded result, or statements not recorded as one. */
export type ABoxSubject = Extraction | UnrecordedInstanceData;

const TABS = ["overview", "provenance"] as const;
type ABoxTab = (typeof TABS)[number];

/**
 * The one view of instance data (F84 H-21, F154): its document's regest, each statement beside
 * the part of the text that says it (`TextWithStatements`, the Documents page's view), a
 * proposal's statements drawn apart from accepted ones; the Turtle one click away; and, for a
 * recorded result, its provenance. The result's page, its review steps and an assembled vita
 * show it alike, before and after acceptance. Where it is decided is its review step's job, not
 * this view's.
 */
export default function ABoxView({ extraction, statementParts }: { extraction: ABoxSubject; statementParts?: (number: number) => ReactNode }) {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState<ABoxTab>("overview");
  const recorded = "decisions" in extraction ? extraction : null;
  const individuals = useMemo(() => groupIndividuals(extraction.instanceStatements, i18n.resolvedLanguage ?? "en").length, [extraction.instanceStatements, i18n.resolvedLanguage]);
  // Each tab has its own Technical details: the Turtle here, the recorded IDs with the provenance.
  const regest = <><ABoxRegest subject={extraction} statementParts={statementParts} /><ABoxTechnicalDetails extraction={recorded ?? extraction} /></>;
  return (
    <section className="app-card space-y-3 p-3" aria-label={t("data.abox.title")}>
      <h3 className="app-title font-semibold">{t("data.abox.counts", { statements: t("data.counts.statements", { count: extraction.instanceStatements.length }), individuals: t("data.counts.individuals", { count: individuals }) })}</h3>
      {recorded === null ? regest : (
        <Tabs label={t("data.abox.title")} options={TABS.map((name) => ({ key: name, label: t(`data.abox.${name}`) }))} selected={tab} onSelect={setTab}>
          {tab === "overview" ? regest : <ProvenancePanel extraction={recorded} />}
        </Tabs>
      )}
    </section>
  );
}

/** The statements drawn on their document's text, once it is read. */
function ABoxRegest({ subject, statementParts }: { subject: ABoxSubject; statementParts: ((number: number) => ReactNode) | undefined }) {
  const { t } = useTranslation();
  const { state, reload } = useSourceDocument(parseDocumentVersionRef(subject.document));
  const accepted = "decisions" in subject ? subject.state === "accepted" : subject.accepted;
  const results = useMemo((): readonly RegestResult[] => [{
    source: subject.source,
    model: subject.model ?? "",
    accepted,
    statements: subject.instanceStatements,
  }], [subject.source, subject.model, accepted, subject.instanceStatements]);
  const extra = useMemo(() => (statementParts === undefined ? undefined : (placed: PlacedStatement) => statementParts(placed.statement.number)), [statementParts]);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />;
  if (state.value === null) return <p>{t("data.abox.sourceUnavailable")}</p>;
  // A recorded result belongs to its project; other statements say which they belong to.
  const project = "decisions" in subject ? subject.projectId : subject.project;
  return <TextWithStatements document={state.value} project={project} results={results} annotations={null} askInProject={subject.projectId} askWith={null} statementExtra={extra} />;
}
